/**
 * Cliente HTTP mínimo contra api.mipuebloapp.com.
 * - lee la base URL desde import.meta.env.VITE_API_BASE
 * - inyecta Authorization: Bearer si hay token en sessionStorage
 * - parsea JSON y centraliza el error con `code` + `detail`.
 */

const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "https://api.mipuebloapp.com";

const TOKEN_KEY = "mp.panel.token";

export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;
  constructor(status: number, detail: unknown, message: string) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

interface RequestOpts {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  auth?: boolean;
}

export async function api<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";

  if (opts.auth !== false) {
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: "omit",
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const detail =
      parsed && typeof parsed === "object" && "detail" in parsed
        ? (parsed as { detail: unknown }).detail
        : parsed;
    const msg =
      typeof detail === "string" ? detail : `HTTP ${res.status}`;
    throw new ApiError(res.status, detail, msg);
  }

  return parsed as T;
}

// ===== Tipos compartidos con el backend =====
export interface LoginResponse {
  access_token: string;
  token_type: string;
  requires_totp: boolean;
  must_change_password: boolean;
}

export interface MeResponse {
  id: string;
  email: string;
  nombre: string;
  rol: string;
  totp_enabled: boolean;
  must_change_password: boolean;
}

export interface TotpEnrollResponse {
  secret_base32: string;
  otpauth_uri: string;
}

// ===== Endpoints =====
export const auth = {
  login: (email: string, password: string, totp_code?: string) =>
    api<LoginResponse>("/admin/auth/login", {
      method: "POST",
      body: totp_code ? { email, password, totp_code } : { email, password },
      auth: false,
    }),
  me: () => api<MeResponse>("/admin/auth/me"),
  changePassword: (current: string, next: string) =>
    api<void>("/admin/auth/change-password", {
      method: "POST",
      body: { current_password: current, new_password: next },
    }),
  totpEnroll: () =>
    api<TotpEnrollResponse>("/admin/auth/totp/enroll", { method: "POST" }),
  totpVerifyEnroll: (code: string) =>
    api<void>("/admin/auth/totp/verify-enroll", {
      method: "POST",
      body: { code },
    }),
  totpDisable: (password: string, code: string) =>
    api<void>("/admin/auth/totp/disable", {
      method: "POST",
      body: { password, code },
    }),
};

// ===== Tenants =====
export type TenantStatus = "trial" | "active" | "suspended" | "cancelled";

export type NivelTenant = "nacional" | "regional" | "gobernacion" | "municipio" | "jac";

export interface Tenant {
  id: string;
  slug: string;
  nombre: string;
  nit: string;
  departamento: string;
  municipio: string;
  codigo_divipola: string | null;
  email_contacto: string;
  telefono: string | null;
  status: TenantStatus;
  nivel: NivelTenant;
  parent_id: string | null;
  derivacion_habilitada: boolean;
  aprobacion_habilitada: boolean;
  created_at: string;
  updated_at: string;
  activado_en: string | null;
  suspendido_en: string | null;
}

export interface TenantListResponse {
  items: Tenant[];
  total: number;
  limit: number;
  offset: number;
}

export interface TenantCreateBody {
  nombre: string;
  nit: string;
  departamento: string;
  municipio: string;
  codigo_divipola?: string;
  email_contacto: string;
  telefono?: string;
  slug?: string;
  nivel?: NivelTenant;
  parent_id?: string | null;
}

export interface TenantUpdateBody {
  nombre?: string;
  departamento?: string;
  municipio?: string;
  codigo_divipola?: string;
  email_contacto?: string;
  telefono?: string;
  status?: TenantStatus;
  nivel?: NivelTenant;
  parent_id?: string | null;
  set_root?: boolean;
  derivacion_habilitada?: boolean;
  aprobacion_habilitada?: boolean;
}

// ===== Audit log =====
export type ActorKind = "super" | "tenant";

export interface AuditEntry {
  id: string;
  tenant_id: string | null;
  actor_kind: ActorKind;
  actor_id: string;
  actor_email: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  changes: Record<string, unknown>;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface AuditListResponse {
  items: AuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

export const audit = {
  list: (
    params: { tenant_id?: string; action?: string; actor_id?: string; limit?: number } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.tenant_id) qs.set("tenant_id", params.tenant_id);
    if (params.action) qs.set("action", params.action);
    if (params.actor_id) qs.set("actor_id", params.actor_id);
    if (params.limit) qs.set("limit", String(params.limit));
    const suffix = qs.toString();
    return api<AuditListResponse>(`/admin/audit-log${suffix ? `?${suffix}` : ""}`);
  },
};

// ===== WhatsApp credentials =====
export interface WhatsappCredsOut {
  tenant_id: string;
  phone_number_id: string;
  waba_id: string | null;
  display_phone_number: string | null;
  verify_token: string;
  has_app_secret: boolean;
  active: boolean;
  webhook_url: string;
  created_at: string;
  updated_at: string;
}

export interface WhatsappCredsBody {
  phone_number_id: string;
  waba_id?: string;
  display_phone_number?: string;
  access_token: string;
  app_secret?: string;
  verify_token?: string;
}

// ===== Funcionarios (super_admin gestiona los de un tenant) =====
export type FuncionarioRol =
  | "admin_tenant"
  | "supervisor"
  | "funcionario"
  | "radicador";

export type TipoDocFuncionario = "CC" | "CE" | "TI" | "PA" | "NIT" | "otro";
export type NivelJerarquico =
  | "directivo"
  | "asesor"
  | "profesional"
  | "tecnico"
  | "asistencial";
export type TipoVinculacion =
  | "carrera"
  | "libre_nombramiento"
  | "provisional"
  | "contrato_ops"
  | "trabajador_oficial";

export interface FuncionarioOut {
  id: string;
  tenant_id: string;
  email: string;
  nombre: string;
  rol: FuncionarioRol;
  dependencia: string | null;
  dependencia_id: string | null;
  tipo_doc: TipoDocFuncionario | null;
  numero_doc: string | null;
  cargo: string | null;
  nivel_jerarquico: NivelJerarquico | null;
  tipo_vinculacion: TipoVinculacion | null;
  telefono: string | null;
  fecha_posesion: string | null;
  acto_administrativo: string | null;
  activo: boolean;
  archivado_at: string | null;
  must_change_password: boolean;
}

export interface FuncionarioCreate {
  email: string;
  nombre: string;
  rol: FuncionarioRol;
  dependencia_id?: string | null;
  tipo_doc?: TipoDocFuncionario | null;
  numero_doc?: string | null;
  cargo?: string | null;
  nivel_jerarquico?: NivelJerarquico | null;
  tipo_vinculacion?: TipoVinculacion | null;
  telefono?: string | null;
  fecha_posesion?: string | null;
  acto_administrativo?: string | null;
  password?: string;
}

export interface FuncionarioCreateResponse extends FuncionarioOut {
  generated_password?: string | null;
}

export interface FuncionarioUpdate {
  nombre?: string;
  rol?: FuncionarioRol;
  dependencia_id?: string | null;
  tipo_doc?: TipoDocFuncionario | null;
  numero_doc?: string | null;
  cargo?: string | null;
  nivel_jerarquico?: NivelJerarquico | null;
  tipo_vinculacion?: TipoVinculacion | null;
  telefono?: string | null;
  fecha_posesion?: string | null;
  acto_administrativo?: string | null;
  activo?: boolean;
}

export interface DependenciaSimple {
  id: string;
  nombre: string;
  activo: boolean;
}

export interface FuncionariosListResponse {
  items: FuncionarioOut[];
  total: number;
  limit: number;
  offset: number;
}

export const funcionarios = {
  list: (tenantId: string, incluirArchivados: boolean = false) =>
    api<FuncionariosListResponse>(
      `/admin/tenants/${tenantId}/funcionarios${incluirArchivados ? "?incluir_archivados=true" : ""}`,
    ),
  create: (tenantId: string, body: FuncionarioCreate) =>
    api<FuncionarioCreateResponse>(`/admin/tenants/${tenantId}/funcionarios`, {
      method: "POST",
      body,
    }),
  update: (tenantId: string, fid: string, body: FuncionarioUpdate) =>
    api<FuncionarioOut>(
      `/admin/tenants/${tenantId}/funcionarios/${fid}`,
      { method: "PATCH", body },
    ),
  listDependencias: (tenantId: string) =>
    api<DependenciaSimple[]>(`/admin/tenants/${tenantId}/dependencias`),
  archivar: (tenantId: string, fid: string) =>
    api<FuncionarioOut>(
      `/admin/tenants/${tenantId}/funcionarios/${fid}/archivar`,
      { method: "POST" },
    ),
  restaurar: (tenantId: string, fid: string) =>
    api<FuncionarioOut>(
      `/admin/tenants/${tenantId}/funcionarios/${fid}/restaurar`,
      { method: "POST" },
    ),
};

// ===== Jarvis · vínculos de WhatsApp (super_admin gestiona por tenant) =====
export interface JarvisSesion {
  id: string;
  usuario_tenant_id: string;
  funcionario_nombre: string;
  funcionario_email: string;
  telefono: string | null;
  verificado: boolean;
  pendiente: boolean;
  verificado_at: string | null;
  created_at: string;
}

export interface JarvisEnrolarOut {
  sesion: JarvisSesion;
  otp_enviado: boolean;
  detalle: string | null;
}

export const jarvis = {
  listSesiones: (tenantId: string) =>
    api<JarvisSesion[]>(`/admin/tenants/${tenantId}/jarvis/sesiones`),
  enrolar: (tenantId: string, body: { usuario_tenant_id: string; telefono: string }) =>
    api<JarvisEnrolarOut>(`/admin/tenants/${tenantId}/jarvis/sesiones`, {
      method: "POST",
      body,
    }),
  eliminar: (tenantId: string, sesionId: string) =>
    api<void>(`/admin/tenants/${tenantId}/jarvis/sesiones/${sesionId}`, {
      method: "DELETE",
    }),
};

export const whatsapp = {
  get: (tenantId: string) =>
    api<WhatsappCredsOut>(`/admin/tenants/${tenantId}/whatsapp`),
  upsert: (tenantId: string, body: WhatsappCredsBody) =>
    api<WhatsappCredsOut>(`/admin/tenants/${tenantId}/whatsapp`, {
      method: "PUT",
      body,
    }),
  deactivate: (tenantId: string) =>
    api<void>(`/admin/tenants/${tenantId}/whatsapp`, { method: "DELETE" }),
  patchWaba: (tenantId: string, waba_id: string) =>
    api<WhatsappCredsOut>(`/admin/tenants/${tenantId}/whatsapp/waba`, {
      method: "PATCH",
      body: { waba_id },
    }),
  patch: (
    tenantId: string,
    body: Partial<{
      phone_number_id: string;
      waba_id: string;
      display_phone_number: string;
      access_token: string;
      app_secret: string;
      verify_token: string;
    }>,
  ) =>
    api<WhatsappCredsOut>(`/admin/tenants/${tenantId}/whatsapp`, {
      method: "PATCH",
      body,
    }),
  // ----- Visor administrativo (Fases 1-4) -----
  status: (tenantId: string) =>
    api<WhatsappAdminStatusOut>(
      `/admin/tenants/${tenantId}/whatsapp/status`,
    ),
  metrics: (tenantId: string) =>
    api<WhatsappMetricsOut>(`/admin/tenants/${tenantId}/whatsapp/metrics`),
  messages: (tenantId: string, limit = 50) =>
    api<WhatsappMessageItem[]>(
      `/admin/tenants/${tenantId}/whatsapp/messages?limit=${limit}`,
    ),
  templates: (tenantId: string, refresh = false) =>
    api<WhatsappTemplateItem[]>(
      `/admin/tenants/${tenantId}/whatsapp/templates${refresh ? "?refresh=true" : ""}`,
    ),
  test: (tenantId: string, body: { to: string; body?: string }) =>
    api<WhatsappTestOut>(`/admin/tenants/${tenantId}/whatsapp/test`, {
      method: "POST",
      body,
    }),
  eventMappings: (tenantId: string) =>
    api<EventMappingOut[]>(
      `/admin/tenants/${tenantId}/whatsapp/event-mappings`,
    ),
  eventMappingPut: (tenantId: string, eventKey: string, body: EventMappingBody) =>
    api<EventMappingOut>(
      `/admin/tenants/${tenantId}/whatsapp/event-mappings/${encodeURIComponent(eventKey)}`,
      { method: "PUT", body },
    ),
  eventMappingDelete: (tenantId: string, eventKey: string) =>
    api<void>(
      `/admin/tenants/${tenantId}/whatsapp/event-mappings/${encodeURIComponent(eventKey)}`,
      { method: "DELETE" },
    ),
};

export interface WhatsappAdminStatusOut {
  configurado: boolean;
  active: boolean;
  display_phone_number: string | null;
  phone_number_id: string | null;
  waba_id: string | null;
  has_app_secret: boolean;
  webhook_url: string | null;
  verify_token: string | null;
  updated_at: string | null;
}

export interface WhatsappMetricsOut {
  enviados: number;
  recibidos: number;
  conversaciones: number;
  fallidos: number;
}

export interface WhatsappMessageItem {
  fecha: string;
  direction: "in" | "out";
  numero: string | null;
  contenido: string;
  kind: string;
  template_name: string | null;
  status: string;
  error_msg: string | null;
}

export interface WhatsappTemplateItem {
  name: string;
  category: string | null;
  language: string;
  status: string;
  components: Array<Record<string, unknown>> | null;
}

export interface WhatsappTestOut {
  ok: boolean;
  message: string;
}

export interface EventMappingOut {
  key: string;
  label: string;
  description: string;
  group: string;
  params: string[];
  template_name: string | null;
  language: string | null;
}

export interface EventMappingBody {
  template_name: string;
  language?: string;
}

export type ChannelType =
  | "telegram"
  | "messenger"
  | "instagram"
  | "email"
  | "webchat";

export interface ChannelStatusOut {
  channel_type: ChannelType;
  active: boolean;
  configurado: boolean;
  display_name: string | null;
  external_id: string | null;
  verify_token: string | null;
  has_app_secret: boolean;
  updated_at: string | null;
  managed_by_platform: boolean;
}

export const canales = {
  list: (tenantId: string) =>
    api<ChannelStatusOut[]>(`/admin/tenants/${tenantId}/canales`),
  upsert: (
    tenantId: string,
    channel_type: ChannelType,
    body: { active: boolean; display_name?: string | null; config: Record<string, unknown> },
  ) =>
    api<ChannelStatusOut>(`/admin/tenants/${tenantId}/canales/${channel_type}`, {
      method: "PUT",
      body,
    }),
  deactivate: (tenantId: string, channel_type: ChannelType) =>
    api<void>(`/admin/tenants/${tenantId}/canales/${channel_type}`, {
      method: "DELETE",
    }),
};

// ===== Impersonación =====
export interface ImpersonateResponse {
  access_token: string;
  tenant_slug: string;
  tenant_nombre: string;
  expires_minutes: number;
  redirect_url: string;
}

export const impersonate = {
  startSupport: (tenantId: string) =>
    api<ImpersonateResponse>(`/admin/tenants/${tenantId}/impersonate`, {
      method: "POST",
    }),
};

export interface PanoramaTrendDay {
  fecha: string;
  radicados: number;
}

export interface PanoramaTopTenant {
  tenant_id: string;
  slug: string;
  nombre: string;
  departamento: string;
  radicados: number;
  cumplimiento_pct: number | null;
}

export interface PanoramaResponse {
  tenants_total: number;
  tenants_por_estado: Record<string, number>;
  tenants_por_departamento: Record<string, number>;
  funcionarios_total: number;
  funcionarios_activos: number;
  pqrsdf_total: number;
  pqrsdf_mes_actual: number;
  pqrsdf_ultimos_7d: number;
  pqrsdf_vencidos_abiertos: number;
  cumplimiento_promedio_pct: number | null;
  trend_30d: PanoramaTrendDay[];
  top_tenants: PanoramaTopTenant[];
  backend_status: "operativo" | "degradado" | "fuera_de_servicio";
  mes_actual: string;
}

export const tenants = {
  panorama: (params: { estado?: string; tenant_id?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.estado) qs.set("estado", params.estado);
    if (params.tenant_id) qs.set("tenant_id", params.tenant_id);
    const suffix = qs.toString();
    return api<PanoramaResponse>(`/admin/panorama${suffix ? `?${suffix}` : ""}`);
  },
  list: (params: { q?: string; status?: TenantStatus; departamento?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.status) qs.set("status", params.status);
    if (params.departamento) qs.set("departamento", params.departamento);
    const suffix = qs.toString();
    return api<TenantListResponse>(`/admin/tenants${suffix ? `?${suffix}` : ""}`);
  },
  get: (id: string) => api<Tenant>(`/admin/tenants/${id}`),
  create: (body: TenantCreateBody) =>
    api<Tenant>("/admin/tenants", { method: "POST", body }),
  update: (id: string, body: TenantUpdateBody) =>
    api<Tenant>(`/admin/tenants/${id}`, { method: "PATCH", body }),
  getPlatformConfig: () =>
    api<PlatformConfig>("/admin/platform/config"),
  updateProviderConfig: (
    provider: AIProviderID,
    body: ProviderUpdateBody,
  ) =>
    api<PlatformConfig>(`/admin/platform/config/${provider}`, {
      method: "PUT",
      body,
    }),
  testProvider: (provider: AIProviderID) =>
    api<{ ok: boolean; detail: string; provider: AIProviderID; model: string | null }>(
      `/admin/platform/config/${provider}/test`,
      { method: "POST" },
    ),
  listProviderModels: (provider: AIProviderID) =>
    api<{ provider: AIProviderID; models: { id: string; label: string }[] }>(
      `/admin/platform/config/${provider}/models`,
    ),
  getEncuestaBase: () => api<EncuestaBaseConfig>("/admin/platform/encuesta-base"),
  updateEncuestaBase: (body: Partial<EncuestaBaseConfig>) =>
    api<EncuestaBaseConfig>("/admin/platform/encuesta-base", {
      method: "PUT",
      body,
    }),
  getBranding: (id: string) =>
    api<TenantBranding>(`/admin/tenants/${id}/branding`),
  updateBranding: (id: string, body: TenantBrandingUpdate) =>
    api<TenantBranding>(`/admin/tenants/${id}/branding`, {
      method: "PATCH",
      body,
    }),
  uploadLogo: async (id: string, file: File): Promise<TenantBranding> => {
    const token = getToken();
    const form = new FormData();
    form.append("file", file, file.name);
    const res = await fetch(`${API_BASE}/admin/tenants/${id}/branding/logo`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: form,
      credentials: "omit",
    });
    if (!res.ok) {
      const text = await res.text();
      throw new ApiError(res.status, text, `HTTP ${res.status}`);
    }
    return (await res.json()) as TenantBranding;
  },
  getAIDraft: (id: string) =>
    api<TenantAIDraft>(`/admin/tenants/${id}/ai-draft`),
  updateAIDraft: (id: string, body: TenantAIDraftUpdate) =>
    api<TenantAIDraft>(`/admin/tenants/${id}/ai-draft`, {
      method: "PATCH",
      body,
    }),
  getGd: (id: string) =>
    api<{
      enabled: boolean;
      actos: boolean;
      correspondencia: boolean;
      expedientes: boolean;
    }>(`/admin/tenants/${id}/gestion-documental`),
  updateGd: (
    id: string,
    body: {
      actos?: boolean;
      correspondencia?: boolean;
      expedientes?: boolean;
    },
  ) =>
    api<{
      enabled: boolean;
      actos: boolean;
      correspondencia: boolean;
      expedientes: boolean;
    }>(`/admin/tenants/${id}/gestion-documental`, {
      method: "PATCH",
      body,
    }),
  getTurnos: (id: string) =>
    api<{ enabled: boolean }>(`/admin/tenants/${id}/turnos`),
  setTurnos: (id: string, enabled: boolean) =>
    api<{ enabled: boolean }>(`/admin/tenants/${id}/turnos`, {
      method: "PATCH",
      body: { enabled },
    }),
  getSoporteRag: (id: string) =>
    api<TenantSoporteRag>(`/admin/tenants/${id}/soporte-rag`),
  updateSoporteRag: (id: string, body: TenantSoporteRagUpdate) =>
    api<TenantSoporteRag>(`/admin/tenants/${id}/soporte-rag`, {
      method: "PATCH",
      body,
    }),
  testSoporteRag: (id: string, query: string) =>
    api<{
      ok: boolean;
      error: string | null;
      respuesta: string | null;
      articulo_slug: string | null;
      escalar: boolean | null;
      kb_count: number;
      kb_titulos: string[];
      latency_ms: number;
    }>(`/admin/tenants/${id}/soporte-rag/test`, {
      method: "POST",
      body: { query },
    }),
  getEncuestasConfig: (id: string) =>
    api<EncuestasConfig>(`/admin/tenants/${id}/encuestas/config`),
  updateEncuestasConfig: (id: string, preguntas: EncuestaPregunta[]) =>
    api<EncuestasConfig>(`/admin/tenants/${id}/encuestas/config`, {
      method: "PUT",
      body: { preguntas },
    }),
  getEncuestasMetrics: (id: string, days: number = 90) =>
    api<EncuestasMetrics>(`/admin/tenants/${id}/encuestas/metrics?days=${days}`),
  getEncuestasRecent: (id: string, limit: number = 20) =>
    api<{ items: EncuestaRecent[]; total: number }>(
      `/admin/tenants/${id}/encuestas/recent?limit=${limit}`,
    ),
  getSLA: (id: string) => api<SLAResponse>(`/admin/tenants/${id}/sla`),
  updateSLA: (id: string, tabla: SLARow[]) =>
    api<SLAResponse>(`/admin/tenants/${id}/sla`, {
      method: "PUT",
      body: { tabla },
    }),
  getSigning: (id: string) => api<SigningStatus>(`/admin/tenants/${id}/signing`),
  toggleSigning: (id: string, enabled: boolean) =>
    api<SigningStatus>(`/admin/tenants/${id}/signing/enabled`, {
      method: "PUT",
      body: { enabled },
    }),
  deleteSigningCert: (id: string) =>
    api<SigningStatus>(`/admin/tenants/${id}/signing/cert`, { method: "DELETE" }),
  platformMetrics: (days: number = 30) =>
    api<PlatformMetrics>(`/admin/platform/metrics?days=${days}`),
  platformMetricsTenantsByFeature: (feature: string) =>
    api<TenantBrief[]>(
      `/admin/platform/metrics/feature/${encodeURIComponent(feature)}/tenants`,
    ),
  uploadSigningCert: async (
    id: string,
    p12: File,
    password: string,
  ): Promise<SigningStatus> => {
    const token = getToken();
    const form = new FormData();
    form.append("p12", p12, p12.name);
    form.append("password", password);
    const res = await fetch(`${API_BASE}/admin/tenants/${id}/signing/cert`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: form,
      credentials: "omit",
    });
    if (!res.ok) {
      let detail: unknown = null;
      try {
        detail = await res.json();
      } catch {
        /* ignore */
      }
      const msg =
        (typeof detail === "object" && detail !== null && "detail" in detail
          ? String((detail as { detail: unknown }).detail)
          : null) || `HTTP ${res.status}`;
      throw new ApiError(res.status, detail, msg);
    }
    return (await res.json()) as SigningStatus;
  },
  uploadEscudo: async (id: string, file: File): Promise<TenantBranding> => {
    const token = getToken();
    const form = new FormData();
    form.append("file", file, file.name);
    const res = await fetch(`${API_BASE}/admin/tenants/${id}/branding/escudo`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: form,
      credentials: "omit",
    });
    if (!res.ok) {
      const text = await res.text();
      throw new ApiError(res.status, text, `HTTP ${res.status}`);
    }
    return (await res.json()) as TenantBranding;
  },
};

export interface TenantBranding {
  logo_path?: string | null;
  escudo_path?: string | null;
  color_primario?: string | null;
  firma_titulo?: string | null;
  firma_nombre?: string | null;
  direccion_institucional?: string | null;
  telefono_institucional?: string | null;
}

export type AIProviderID =
  | "anthropic"
  | "openai"
  | "gemini"
  | "lm_studio"
  | "ollama";

export interface ProviderStatus {
  provider: AIProviderID;
  configured: boolean;
  api_key_fp: string | null;
  base_url: string | null;
  requires_base_url: boolean;
  has_env_fallback: boolean;
}

export interface PlatformConfig {
  providers: ProviderStatus[];
}

export interface ProviderUpdateBody {
  api_key?: string;
  base_url?: string;
}

export interface ResolvioOption {
  id: string;
  label: string;
  desc: string;
  positiva: boolean;
}

export interface EncuestaBaseConfig {
  intro: string;
  rating_label: string;
  resolvio_label: string;
  resolvio_options: ResolvioOption[];
  comentario_label: string;
  comentario_placeholder: string;
  comentario_required: boolean;
}

export interface SLARow {
  tipo: string;
  prioridad: string;
  dias_habiles: number;
}

export interface SLAResponse {
  tabla: SLARow[];
  legal_max: Record<string, number>;
  tipos: string[];
  prioridades: string[];
}

export interface SigningStatus {
  has_cert: boolean;
  enabled: boolean;
  subject: string | null;
  valid_until: string | null;
}

export interface ChurnPoint {
  mes: string;
  nuevos: number;
  cancelados: number;
  netos: number;
}

export interface AdopcionFeature {
  feature: string;
  tenants_con: number;
  tenants_total: number;
  pct: number;
}

export interface TenantBrief {
  id: string;
  slug: string;
  nombre: string;
  departamento: string;
  municipio: string;
  status: string;
  created_at: string;
  activado_en: string | null;
}

export interface PlatformMetrics {
  tenants_facturables: number;
  tenants_active: number;
  tenants_trial: number;
  tenants_suspended: number;
  tenants_cancelled: number;
  trial_to_active_pct: number | null;
  churn_30d_pct: number | null;
  churn_serie_12m: ChurnPoint[];
  pqrsdf_total: number;
  pqrsdf_mes_actual: number;
  funcionarios_activos: number;
  encuestas_respondidas: number;
  rating_promedio_plataforma: number | null;
  kb_articulos_publicados: number;
  adopcion: AdopcionFeature[];
}

export interface EncuestaPregunta {
  id?: string;
  tipo: "choice" | "text" | "boolean";
  label: string;
  required?: boolean;
  options?: string[];
}

export interface EncuestasConfig {
  preguntas: EncuestaPregunta[];
  max_preguntas: number;
  tipos_permitidos: string[];
}

export interface EncuestasMetrics {
  enviadas: number;
  respondidas: number;
  tasa_respuesta: number;
  rating_promedio: number | null;
  resolvio_si_pct: number | null;
  resolvio_parcial_pct: number | null;
  resolvio_no_pct: number | null;
  detractores: number;
  promotores: number;
  nps_aprox: number | null;
}

export interface EncuestaRecent {
  id: string;
  pqrsdf_id: string;
  radicado: string;
  asunto: string;
  rating: number | null;
  resolvio: "si" | "parcial" | "no" | null;
  comentario: string | null;
  respondida_at: string | null;
  respuestas_extra?: Record<string, { label: string; tipo: string; valor: unknown }>;
}

export interface TenantAIDraft {
  enabled: boolean;
  provider: AIProviderID;
  model: string;
  permite_pii_externa: boolean;
  /** Asistente por WhatsApp (Jarvis): reusa este proveedor/modelo de IA. */
  jarvis_enabled: boolean;
  /** Capacidades de operación de Jarvis (citas, expedientes, …). */
  jarvis_capacidades: Record<string, boolean>;
}

export interface TenantAIDraftUpdate {
  enabled?: boolean;
  provider?: AIProviderID;
  model?: string;
  permite_pii_externa?: boolean;
  jarvis_enabled?: boolean;
  jarvis_capacidades?: Record<string, boolean>;
}

export interface TenantSoporteRag extends TenantAIDraft {
  permite_pii_externa: boolean;
}

export interface TenantSoporteRagUpdate extends TenantAIDraftUpdate {
  permite_pii_externa?: boolean;
}

export interface TenantBrandingUpdate {
  color_primario?: string;
  firma_titulo?: string;
  firma_nombre?: string;
  direccion_institucional?: string;
  telefono_institucional?: string;
}
